import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { devtools } from 'zustand/middleware'
import { ASSIGNMENT_TYPE_CONFIG, ASSIGNMENT_TYPES } from './constants'

// 初始状态
const initialState = {
  selectedType: null,
  selectedUsers: [],
  selectedRoles: [],
  selectedDepts: [],
  expression: ''
}

// 创建UserAssignmentSelector专用的组件级别store
export const useUserAssignmentStore = create(
  devtools(
    immer((set, get) => ({
      // 状态
      ...initialState,
      
      // Actions
      setSelectedType: (type) => set((state) => {
        state.selectedType = type
        
        // 根据选择的类型清理不相关的选择
        if (type !== ASSIGNMENT_TYPES.STATIC_USER) {
          state.selectedUsers = []
        }
        if (type !== ASSIGNMENT_TYPES.STATIC_ROLE && type !== ASSIGNMENT_TYPES.DYNAMIC_DEPT_ROLE) {
          state.selectedRoles = []
        }
        if (type !== ASSIGNMENT_TYPES.STATIC_DEPT && type !== ASSIGNMENT_TYPES.DYNAMIC_DEPT_ROLE) {
          state.selectedDepts = []
        }
        if (type !== ASSIGNMENT_TYPES.EXPRESSION) {
          state.expression = ''
        }
      }),
      
      setSelectedUsers: (users) => set((state) => {
        state.selectedUsers = users
      }),
      
      setSelectedRoles: (roles) => set((state) => {
        state.selectedRoles = roles
      }),
      
      setSelectedDepts: (depts) => set((state) => {
        state.selectedDepts = depts
      }),
      
      setExpression: (expression) => set((state) => {
        state.expression = expression
      }),
      
      // 初始化方法
      initialize: (initialValue) => set((state) => {
        if (initialValue) {
          // 支持完整的数据结构，包括从getCurrentConfig返回的格式
          state.selectedType = initialValue.type || null
          state.selectedUsers = initialValue.users || []
          state.selectedRoles = initialValue.roles || []
          state.selectedDepts = initialValue.depts || []
          state.expression = initialValue.expression || ''
          // 注意：label和isDynamic字段不需要存储，它们是从配置中计算得出的
        } else {
          // 重置为初始状态
          Object.assign(state, initialState)
        }
      }),
      
      // 重置方法
      reset: () => set((state) => {
        Object.assign(state, initialState)
      }),
      
      // 获取当前配置的方法
      getCurrentConfig: () => {
        const state = get()
        
        // 获取类型配置
        const typeConfig = ASSIGNMENT_TYPE_CONFIG[state.selectedType]
        
        if (!typeConfig) {
          return null
        }
        
        // 基础配置
        const config = {
          type: state.selectedType,
          label: typeConfig.label,
          isDynamic: typeConfig.isDynamic
        }
        
        // 根据配置需求和数据有效性添加条件字段
        if (typeConfig.needsUserSelect && state.selectedUsers?.length > 0) {
          config.users = state.selectedUsers
        }
        
        if (typeConfig.needsRoleSelect && state.selectedRoles?.length > 0) {
          config.roles = state.selectedRoles
        }
        
        if (typeConfig.needsDeptSelect && state.selectedDepts?.length > 0) {
          config.depts = state.selectedDepts
        }
        
        if (typeConfig.needsExpression && state.expression?.trim()) {
          config.expression = state.expression
        }
        
        return config
      },
      
      // 验证当前配置是否有效
      validateConfig: () => {
        const state = get()
        
        if (!state.selectedType) {
          return { valid: false, message: '请选择分配类型' }
        }
        
        switch (state.selectedType) {
          case ASSIGNMENT_TYPES.STATIC_USER:
            if (!state.selectedUsers || state.selectedUsers.length === 0) {
              return { valid: false, message: '请选择用户' }
            }
            break
          case ASSIGNMENT_TYPES.STATIC_ROLE:
            if (!state.selectedRoles || state.selectedRoles.length === 0) {
              return { valid: false, message: '请选择角色' }
            }
            break
          case ASSIGNMENT_TYPES.STATIC_DEPT:
            if (!state.selectedDepts || state.selectedDepts.length === 0) {
              return { valid: false, message: '请选择部门' }
            }
            break
          case ASSIGNMENT_TYPES.DYNAMIC_DEPT_ROLE:
            if ((!state.selectedDepts || state.selectedDepts.length === 0) && 
                (!state.selectedRoles || state.selectedRoles.length === 0)) {
              return { valid: false, message: '请选择部门或角色' }
            }
            break
          case ASSIGNMENT_TYPES.EXPRESSION:
            if (!state.expression || state.expression.trim() === '') {
              return { valid: false, message: '请输入表达式' }
            }
            break
          case ASSIGNMENT_TYPES.DYNAMIC_USER_ROLE:
          case ASSIGNMENT_TYPES.DYNAMIC_USER_DEPT:
          case ASSIGNMENT_TYPES.DYNAMIC_INITIATOR:
          case ASSIGNMENT_TYPES.DYNAMIC_PREVIOUS:
            // 动态分配类型不需要额外验证
            break
          default:
            return { valid: false, message: '未知的分配类型' }
        }
        
        return { valid: true }
      }
    })),
    {
      name: 'user-assignment-selector', // DevTools中显示的名称
    }
  )
)

// 导出分配类型常量
// ASSIGNMENT_TYPES 现在从 constants.js 导出

// 选择器函数，用于优化性能
export const selectSelectedType = (state) => state.selectedType
export const selectSelectedUsers = (state) => state.selectedUsers
export const selectSelectedRoles = (state) => state.selectedRoles
export const selectSelectedDepts = (state) => state.selectedDepts
export const selectExpression = (state) => state.expression
export const selectActions = (state) => ({
  setSelectedType: state.setSelectedType,
  setSelectedUsers: state.setSelectedUsers,
  setSelectedRoles: state.setSelectedRoles,
  setSelectedDepts: state.setSelectedDepts,
  setExpression: state.setExpression,
  initialize: state.initialize,
  reset: state.reset,
  getCurrentConfig: state.getCurrentConfig,
  validateConfig: state.validateConfig
})